// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import cloneDeep from "lodash/cloneDeep";

/**
 * True while the PDP is rendered against the variant *parent* because the initial variant has not been
 * resolved yet.
 *
 * The base DisplayProduct chain only dispatches UpdateVariantSelection (which sets `selectedProductId`)
 * from InitVariantProduct, after real-time pricing and inventory have come back. Since the PDP now renders
 * as soon as the product itself is loaded, this flags the window where any pricing-derived UI would be
 * showing the parent's placeholder values, and should show a spinner instead.
 *
 * Non-variant products get `selectedProductId` at CompleteLoadProduct, so this is always false for them.
 */
export const xIsInitialVariantSelectionPending = (state: ApplicationState, product?: ProductModel | null) =>
    !state.pages.productDetails.selectedProductId && !!product?.variantTypeId;

export const isProductQuoteRequired = (product: ProductModel, productInfo: ProductInfo) => {
    return (
        product.quoteRequired ||
        (productInfo?.pricing?.unitNetPrice !== undefined && productInfo.pricing.unitNetPrice === 0)
    );
};

export const isExpeditedForm = (product: ProductModel, productInfo: ProductInfo) => {
    return (
        !product.quoteRequired &&
        !!productInfo.pricing?.additionalResults?.["leadTime"] &&
        Number(productInfo.pricing.additionalResults["leadTime"]) > 14 &&
        Number(productInfo.pricing.additionalResults["availableQuantity"]) <= 0
    );
};

export const getCustomBadge = (product: ProductModel) => product.properties?.badge;

// XNG-Changes: add properties to OOTB getComputedVariantProduct
export function xGetComputedVariantProduct(
    parentProductState: ReturnType<typeof getProductStateByPath>,
    variantProductState: ReturnType<typeof getProductState>,
) {
    if (!variantProductState.value) {
        return parentProductState;
    }

    if (!parentProductState.value) {
        return parentProductState;
    }

    if (parentProductState.value.id === variantProductState.value.id) {
        return parentProductState;
    }

    const { value: variantProduct } = variantProductState;

    const computedProduct = cloneDeep(parentProductState.value);
    Object.assign(computedProduct, {
        productNumber: variantProduct.productNumber,
        customerProductNumber: variantProduct.customerProductNumber,
        smallImagePath: variantProduct.smallImagePath,
        mediumImagePath: variantProduct.mediumImagePath,
        largeImagePath: variantProduct.largeImagePath,
        imageAltText: variantProduct.imageAltText,
        id: variantProduct.id,
        quoteRequired: variantProduct.quoteRequired,
        productTitle: variantProduct.productTitle,
        images: variantProduct.images,
        trackInventory: variantProduct.trackInventory,
        minimumOrderQty: variantProduct.minimumOrderQty,
        canonicalUrl: variantProduct.canonicalUrl,
        unitOfMeasures: variantProduct.unitOfMeasures,
        isVariantParent: false,
        canAddToWishlist: variantProduct.canAddToWishlist,
        cantBuy: variantProduct.cantBuy,
        allowZeroPricing: variantProduct.allowZeroPricing,
        manufacturerItem: variantProduct.manufacturerItem,
        detail: variantProduct.detail,
        content:
            variantProduct.content && variantProduct.content.htmlContent !== ""
                ? variantProduct.content
                : parentProductState.value.content,
        documents:
            variantProduct.documents && variantProduct.documents.length > 0
                ? variantProduct.documents.concat(parentProductState.value.documents || [])
                : parentProductState.value.documents,
        specifications:
            variantProduct.specifications && variantProduct.specifications.length > 0
                ? variantProduct.specifications
                : parentProductState.value.specifications,
        attributeTypes:
            variantProduct.attributeTypes && variantProduct.attributeTypes.length > 0
                ? variantProduct.attributeTypes
                : parentProductState.value.attributeTypes,
        badges:
            variantProduct.badges && variantProduct.badges.length > 0
                ? variantProduct.badges
                : parentProductState.value.badges,
        // XNG-Changes
        unitListPrice: variantProduct.unitListPrice,
        properties: {
            ...computedProduct.properties,
            hasSale: variantProduct.properties?.hasSale,
        },
    });

    return {
        isLoading: parentProductState.isLoading,
        id: variantProductState.id,
        value: computedProduct,
    };
}
